import type { CellState } from '@aj/core';
import { Box, Button, HStack, Text, VStack } from '@aj/ui';
import type { DayCell, DayColumn } from '@aj/ui';
import { DayStrip, DayStripHeader, LEAD_WIDTH } from '@aj/ui';
import { useMemo, useState } from 'react';

import { useAppData } from '../data/AppDataProvider.js';
import { useHabitGrid, useJatraView, useSeries, useWeeklyReview } from '../data/hooks.js';
import { GoalCard } from './jatra/GoalCard.js';
import { JourneyPath } from './jatra/JourneyPath.js';
import { WeeklyReview } from './jatra/WeeklyReview.js';
import { PartsList } from './jatra/PartsList.js';
import { SeriesCard } from './jatra/SeriesCard.js';
import { YearCard } from './jatra/YearCard.js';
import { GoalCompose } from './goal/GoalCompose.js';
import { useElementWidth } from '../hooks/useElementWidth.js';
import { dateToMs } from '../lib/dates.js';
import { fitDays } from './home/fitDays.js';
import { BOTTOM_CLEARANCE } from '../lib/layout.js';
import { useI18n, useT } from '../providers/I18nProvider.js';
import type { MessageKey } from '@aj/i18n';

/**
 * যাত্রা — the live journey (§47).
 *
 * Reads as ONE DOCUMENT, top to bottom, answering three questions in order:
 * where am I going, how far have I come, and what does the record show. The
 * previous version was five identically-weighted cards, which is a settings
 * page: nothing led, so the eye had no entry point.
 *
 * Two widths, deliberately. Prose and stats sit at reading width; the wall
 * chart and the year map run full-bleed with their own scroll, because
 * clamping them to the text column is what made them cramped.
 *
 * The largest thing on the screen is the DESTINATION, in words. Not the XP.
 * Jatra answers "where am I going"; opening on a score answers "how am I
 * doing", which is the scoreboard mindset arriving through the front door.
 *
 * Visited weekly, on purpose (§46). The weekly Review will land here.
 */

/** Every square says what it is out loud — one hue can't. */
const cellKey: Record<CellState, MessageKey> = {
  done: 'grid.done',
  floor: 'grid.floor',
  missed: 'grid.missed',
  off: 'grid.off',
  future: 'grid.future',
};

/** Reading measure for prose and stats. The chart is exempt. */
const COLUMN = 'mx-auto w-full max-w-2xl';

export function Jatra() {
  const t = useT();
  const i18n = useI18n();
  const view = useJatraView();
  const seriesList = useSeries();
  const review = useWeeklyReview();
  const { features } = useAppData();
  const [composing, setComposing] = useState(false);

  const [measure, width] = useElementWidth();
  const grid = useHabitGrid(features.progress.habitGrid ? fitDays(width - 40) : 0);

  const columns = useMemo<DayColumn[]>(
    () =>
      grid.dates.map((date) => ({
        key: date,
        weekday: i18n.formatDate(dateToMs(date), { weekday: 'narrow' }),
        day: i18n.formatDate(dateToMs(date), { day: 'numeric' }),
        isToday: false,
        // Read-only here. Today's tick belongs on Today; a grid you can edit
        // from two screens is two write paths for one act.
        editable: false,
      })),
    [grid.dates, i18n],
  );

  const cells = useMemo<Record<string, DayCell[]>>(() => {
    const dates = grid.dates.map((date) =>
      i18n.formatDate(dateToMs(date), { day: 'numeric', month: 'short' }),
    );
    return Object.fromEntries(
      grid.rows.map((row) => [
        row.id,
        row.cells.map((state, index) => ({
          state,
          label: t(cellKey[state], { name: row.name, date: dates[index] ?? '' }),
        })),
      ]),
    );
  }, [grid.rows, grid.dates, t, i18n]);

  const { comebacks } = view;

  return (
    <Box as="main" className={`min-h-0 flex-1 overflow-y-auto ${BOTTOM_CLEARANCE}`}>
      <VStack gap={0} className="w-full">
        {/* No rank chip here. The route below already names the current
            stop, and showing the rank twice makes it the headline — which is
            the scoreboard §53.8 keeps out. */}
        <Box className={`${COLUMN} px-5 pt-5`}>
          <Text as="h1" variant="title" tone="ink">
            {t('jatra.title')}
          </Text>
        </Box>

        {/* ── Where am I going ─────────────────────────────────────────── */}
        <Section className={COLUMN}>
          {composing ? (
            <GoalCompose onDone={() => setComposing(false)} onCancel={() => setComposing(false)} />
          ) : view.goal ? (
            <VStack gap={5}>
              <GoalCard goal={view.goal} />
              <PartsList goal={view.goal} />
            </VStack>
          ) : (
            <VStack gap={3} align="start">
              <Text variant="body" tone="muted">
                {t('jatra.goalEmpty')}
              </Text>
              <Button icon="map" onClick={() => setComposing(true)}>
                {t('jatra.setGoal')}
              </Button>
            </VStack>
          )}
        </Section>

        {/* The loop closing: the only place the record is read back and the
            plan changes because of it. Above the durable facts, because a
            week just gone is more actionable than a thirty-day average. */}
        {review && (
          <Section className={COLUMN}>
            <WeeklyReview
              review={review}
              goalId={view.goal?.id ?? null}
              onDone={() => undefined}
            />
          </Section>
        )}

        {/* ── How far have I come ──────────────────────────────────────── */}
        <Section className={COLUMN}>
          <JourneyPath view={view} />
        </Section>

        {/* ── The two durable facts ────────────────────────────────────────
            Neither can be destroyed by one bad day, which is the entire
            reason these are the two that get to be here. Equal weight, one
            hue, no display numerals: they are facts, not trophies. */}
        <Section className={COLUMN}>
          <HStack gap={6} align="start" className="flex-wrap">
            {view.commitment && (
              <VStack gap={1} className="min-w-40 flex-1">
                <Text variant="overline" tone="subtle">
                  {t('jatra.keptTitle')}
                </Text>
                <Text variant="heading" tone="ink" numeric>
                  {t('home.percent', { value: Math.round(view.commitment.ratio * 100) })}
                </Text>
                <Text variant="caption" tone="muted">
                  {t('jatra.keptBody', {
                    kept: Math.round(view.commitment.kept),
                    promised: Math.round(view.commitment.promised),
                    days: view.commitment.days,
                  })}
                </Text>
              </VStack>
            )}

            <VStack gap={1} className="min-w-40 flex-1">
              <Text variant="overline" tone="subtle">
                {t('jatra.comebackTitle')}
              </Text>
              {comebacks.gaps === 0 ? (
                <Text variant="body" tone="muted">
                  {t('jatra.comebackNone')}
                </Text>
              ) : (
                <>
                  <Text variant="heading" tone="ink" numeric>
                    {t('jatra.comebackOf', {
                      returns: comebacks.returns,
                      gaps: comebacks.gaps,
                    })}
                  </Text>
                  {comebacks.longestReturn > 0 && (
                    <Text variant="caption" tone="muted">
                      {t('jatra.comebackLongest', { count: comebacks.longestReturn })}
                    </Text>
                  )}
                  {comebacks.away && (
                    <Text variant="caption" tone="subtle">
                      {t('jatra.comebackAway')}
                    </Text>
                  )}
                </>
              )}
            </VStack>
          </HStack>
        </Section>

        {/* ── The record ───────────────────────────────────────────────────
            Full-bleed. Clamping the chart to the reading column is what made
            it cramped; it gets the window and scrolls itself. */}
        <Box ref={measure} className="w-full border-t border-line px-5 pt-6 pb-6">
          <VStack gap={4}>
            <Text variant="overline" tone="subtle">
              {t('jatra.recordTitle')}
            </Text>

            {grid.rows.length === 0 ? (
              <VStack gap={2}>
                <Text variant="label" tone="ink">
                  {t('jatra.empty.title')}
                </Text>
                <Text variant="body" tone="muted">
                  {t('jatra.empty.body')}
                </Text>
              </VStack>
            ) : (
              <Box className="overflow-x-auto">
                <Box className="w-max">
                  <HStack gap={0} align="center">
                    <Box className={`${LEAD_WIDTH} shrink-0`} />
                    <DayStripHeader columns={columns} />
                  </HStack>

                  <VStack as="ul" gap={0} aria-label={t('jatra.recordTitle')}>
                    {grid.rows.map((row) => (
                      <HStack
                        as="li"
                        key={row.id}
                        gap={0}
                        align="center"
                        className="h-11 border-b border-line"
                      >
                        <Text
                          variant="label"
                          tone="ink"
                          className={`${LEAD_WIDTH} shrink-0 truncate pr-3 text-sm`}
                        >
                          {row.name}
                        </Text>
                        <DayStrip columns={columns} cells={cells[row.id] ?? []} />
                      </HStack>
                    ))}
                  </VStack>
                </Box>
              </Box>
            )}
          </VStack>
        </Box>

        {/* ── Trends ───────────────────────────────────────────────────── */}
        <Section className={COLUMN}>
          <SeriesCard series={seriesList} />
        </Section>

        {/* Last, and deliberately so: the most impressive thing on the screen
            and the least actionable. Nothing about last March changes what
            happens tomorrow. */}
        {features.progress.yearGrid && grid.rows.length > 0 && (
          <Box className="w-full border-t border-line px-5 pt-6">
            <YearCard />
          </Box>
        )}
      </VStack>
    </Box>
  );
}

/**
 * A band of the document, separated by a hairline rather than boxed.
 *
 * Cards were the problem with the old screen: five of them at identical
 * weight read as a form, and each one's padding stole width from the chart
 * inside it. Rules divide without competing.
 */
function Section({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <Box className="w-full border-t border-line first:border-t-0">
      <Box className={`${className ?? ''} px-5 py-6`}>{children}</Box>
    </Box>
  );
}
